import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Avatar, Tag, Flex } from "antd";
import { Error } from "../../../commons/Messages/Messages";
import axios from "axios";
import { SocketContext } from "../../../context/SocketContext";
import DropdownComp from "../../../commons/Dropdowns/DropdownComp";
import GeneralSegmented from "../../../commons/GeneralSegmented";
import GeneralTable from "../../../commons/GeneralTable";
import defaultImage from "../../../commons/PanelAssets/img/doctors/doctor-01.jpg";
import SelectComp from "../../../commons/Select/Select";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import ProjectsGridView from "../../../commons/Projects/ProjectsGridView/ProjectsGridView";
import AddEditProject from "../../../commons/Projects/AddEditProject";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import PageToolbar from "../../../design-system/PageToolbar";
import "./AdminProjects.css";

import { useTranslation } from "react-i18next";
const Projects = () => {
  const { t } = useTranslation("dashboard");
  const socket = useContext(SocketContext);
  const router = useHistory();
  const [data, setData] = useState([]);
  const [layout, setLayout] = useState("list");
  const [totalCount, setTotalCount] = useState("");
  const [searchText, setSearchText] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(6);

  const handleSearch = value => {
    setSearchText(value);
    setPageNo(1);
  };

  const columns = [
    {
      title: t("Custom ID"),
      dataIndex: "_id",
      mobile: "subtitle",
      sorter: (a, b) => a._id.length - b._id.length
    },
    {
      title: t("Name"),
      dataIndex: "projectName",
      mobile: "title",
      render: (text, record) => (
        <Link to={`/user/projects/${record?._id}`} className="admin-projects__name">
          {record.projectName}
        </Link>
      ),
      sorter: (a, b) => a.projectName.localeCompare(b.projectName)
    },
    {
      title: t("Service Provider"),
      dataIndex: "service Provider",
      render: (text, record) => (
        <Flex gap={8} align="center">
          <Avatar
            size={40}
            alt=""
            src={
              record.serviceProvider?.image?.url ? record.serviceProvider?.image?.url : defaultImage
            }
          />
          <span>
            {record?.serviceProvider?.firstName} {record?.serviceProvider?.lastName}
          </span>
        </Flex>
      ),
      sorter: (a, b) => a?.serviceProvider?.firstName.localeCompare(b?.serviceProvider?.firstName)
    },
    {
      title: t("Start Date"),
      dataIndex: "startDate"
    },
    {
      title: t("End Date"),
      dataIndex: "endDate"
    },
    {
      title: t("Priority"),
      dataIndex: "priority",
      sorter: (a, b) => a.priority.localeCompare(b?.localeCompare),
      render: (text, record) =>
        record.priority === "MEDIUM" ? (
          <Tag color="orange">{t("MEDIUM")}</Tag>
        ) : record.priority === "HIGH" ? (
          <Tag color="red">{t("HIGH")}</Tag>
        ) : (
          <Tag color="green">{t("LOW")}</Tag>
        )
    },
    {
      title: t("Status"),
      dataIndex: "status",
      sorter: (a, b) => a.status.localeCompare(b?.status),
      render: text => (
        <div className="admin-projects__status">
          <SelectComp
            handleChange={() => undefined}
            value="Active"
            options={[
              {
                value: "JUST ADDED",
                label: t("JUST ADDED")
              },
              {
                value: "IN PROGRESS",
                label: t("IN PROGRESS")
              },
              {
                value: "ISSUES",
                label: t("ISSUES")
              },
              {
                value: "REJECTED",
                label: t("REJECTED")
              },
              {
                value: "COMPLETED",
                label: t("COMPLETED")
              },
              {
                value: "REVISION",
                label: t("REVISION")
              }
            ]}
          />
        </div>
      )
    },
    {
      title: t("Action"),
      key: "action",
      render: (text, obj) => {
        const menuItems = [
          {
            key: "view",
            link: `/user/projects/${obj?._id}`
          },
          {
            key: "edit",
            component: () => <AddEditProject data={obj} updateParent={getData} />
          },
          {
            key: "delete",
            component: () => <DeleteModal text="Project" onClick={() => undefined} />
          }
        ];
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  const getData = async () => {
    await axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/projects/limited`,
        {
          // A super admin sees every project in the tenant (like GET /projects); filtering by
          // their own id showed only projects they happened to own, i.e. usually none.
          searchText,
          pageSize,
          pageNo: pageNo - 1
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      )
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          setData(res.data.data);
          setTotalCount(res.data.count);
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        if (err.response?.data?.errorMessage) {
          Error(err.response?.data?.errorMessage);
        } else {
          Error(err.message);
        }
      });
  };

  const refreshData = () => {
    getData();
  };

  useEffect(() => {
    if (socket) {
      socket.on("refresh service provider data", msg => {
        if (msg) {
          refreshData();
        }
      });
    }
  }, [socket]);

  useEffect(() => {
    getData();

    return () => {};
  }, [searchText, pageNo]);

  return (
    <div className="admin-projects">
      <PageToolbar
        filters={<SearchBarComp handleSearch={handleSearch} />}
        actions={
          <>
            <GeneralSegmented setLayout={setLayout} />
            <AddEditProject updateParent={getData} />
          </>
        }
      />

      {layout === "grid" ? (
        <ProjectsGridView projects={data} />
      ) : (
        <GeneralTable
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={totalCount}
          data={data}
          columns={columns}
          setPageNo={setPageNo}
        />
      )}
    </div>
  );
};

export default Projects;
